/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2024 Prodeo - All Rights Reserved
 */
/* ===== External Libraries ===== */
import { APIError, AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, dayjs, FORMAT_DATE_API, UUID } from '@umanforge/common';
import { AxiosResponse, AxiosError } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';

/* ===== Interfaces and Types ===== */
import { Engagement } from '../interfaces/engagement.interface';
import { EventCommitment, NotArchivedEngagement, PostEventCommitment, PutEventCommitment } from 'interfaces/types.interface';

/* ===== Services ===== */
import { GlobalService } from './global.service';

export class EngagementService {

  /* ===== Send a request to get an engagement by ID ===== */
  static getEngagementById(
    engagementId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Event> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.ENGAGEMENTS}/${engagementId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send a request to fetch engagements ===== */
  static getEngagements(
    eventManagerId: string, startDate: dayjs.Dayjs, endDate: dayjs.Dayjs,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventCommitment[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.ENGAGEMENTS}/${eventManagerId}/event-managers/${startDate.format( FORMAT_DATE_API )}/${endDate.format( FORMAT_DATE_API )}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventCommitment[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send a request to create an engagement ===== */
  static createEngagement(
    engagement: Engagement,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string> {
    return new Promise( async resolve => {
      const body = {
        eventId: engagement.eventId,
        eventManagerIds: engagement.eventManagerIds,
        participants: engagement.participants,
        deliveryDate: engagement.deliveryDate,
      };

      AXIOS.post( `${API.ENGAGEMENTS}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data.id );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to update engagement data ===== */
  static updateEngagementAPI(
    engagementId: UUID, body: PutEventCommitment,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {

      await AXIOS.put( `${API.ENGAGEMENTS}/${engagementId}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  /* ===== Send request to managed engagements of an event ===== */
  static managedEngagementAPI(
    eventId: UUID,
    deleteBody: UUID[], putBody: ( PutEventCommitment & { id: UUID } )[], postBody: PostEventCommitment[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      const body = {
        post: postBody.length === 0 ? null : postBody,
        put: putBody.length === 0 ? null : putBody,
        delete: deleteBody.length === 0 ? null : deleteBody
      };
      await AXIOS.put( `${API.ENGAGEMENTS}/managed_engagements/event/${eventId}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Check if an engagement can be scheduled on a given date (generic: date + event managers + participants) ===== */
  static checkEngagementDateAvailability(
    eventManagerIds: string[],
    participants: string[],
    deliveryDate: dayjs.Dayjs,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
  ): Promise<{ isPossible: boolean, errorMessage: string }> {
    return new Promise( async resolve => {
      const params = {
        eventManagerIds,
        participants,
        date: deliveryDate.format( FORMAT_DATE_API )
      };
      AXIOS.get( `${API.ENGAGEMENTS}/is-engagement-possible`, { ...await CommonGlobalService.getHeaderRequest( token ), params } )
        .then( ( response: AxiosResponse<{ isPossible: boolean } | APIError> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          let errorMessage: string = '';
          let isPossible: boolean = false;
          if ( response.status === 202 ) {
            const error = response.data as APIError;
            isPossible = false;
            errorMessage = error.msg;
          } else {
            const data = response.data as { isPossible: boolean };
            isPossible = data.isPossible;
            errorMessage = '';
          }

          resolve( { isPossible, errorMessage } );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( { isPossible: false, errorMessage: err.message } );
        } );
    } );
  }

  /* ===== Check if engagement is shared across multiple tables ===== */
  static isEngagementSharedAcrossMultipleTables = ( engagement: EventCommitment | Engagement ): boolean => {
    return engagement.eventManagerIds && engagement.eventManagerIds.length > 1;
  };

  /* ===== Get not-archived engagements for a board (paginated) ===== */
  static getNotArchivedEngagements(
    eventManagerId: UUID,
    page: number,
    step: number,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<{ total: number; list: NotArchivedEngagement[] }> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.ENGAGEMENTS}/not-archived/${eventManagerId}/${page}/${step}`,
        await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ total: number; list: NotArchivedEngagement[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( { total: 0, list: [] } );
        } );
    } );
  }

  /* ===== Get not-archived engagements by chart section (paginated) ===== */
  static getNotArchivedEngagementsByChartSection(
    chartSectionId: UUID,
    page: number,
    step: number,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<{ total: number; list: NotArchivedEngagement[] }> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.ENGAGEMENTS}/charts/${chartSectionId}/${page}/${step}`,
        await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ total: number; list: NotArchivedEngagement[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( { total: 0, list: [] } );
        } );
    } );
  }

}
